import React, { useState, useEffect } from 'react';
import {
  Send, Search, Phone, CheckSquare, Square, Tag, MessageSquare,
  Calendar, Zap, Loader2, CheckCircle2, AlertCircle, X, Bookmark, Trash2
} from 'lucide-react';
import { auth } from '../config/firebase';
import { clienteService, lembreteService } from '../services/api';
import CriarLembreteAvulso from './CriarLembreteAvulso';

const MENSAGENS_PREDEFINIDAS = [
  { titulo: "Cliente Sumido", texto: "Vixe, já faz um tempo desde o nosso último atendimento de {servico}... tá precisando dar uma geral essa semana? me avisa aqui pra gente combinar o dia! 😉" },
  { titulo: "Retorno de Rotina", texto: "Opa, {nome}! passando pra lembrar que já tá no tempo do seu retorno para fazer seu atendimento de {servico}. vamos deixar logo marcado para garantir sua vaga?" },
  { titulo: "Promoção/Novidade", texto: "Olá, {nome}! temos uma novidade especial pra você em {servico}. responde aqui que a gente te conta os detalhes!" },
];

const formatarTelefoneExibicao = (raw) => {
  let value = (raw || '').replace(/\D/g, '');
  if (value.length > 11 && value.startsWith('55')) value = value.slice(2);
  if (value.length > 11) value = value.slice(0, 11);
  if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
  if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
  return value;
};

const LembretesMarketingTab = () => {
  const [modo, setModo] = useState('individual'); // 'individual' | 'massa'
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  const [clienteLembrete, setClienteLembrete] = useState(null);

  const [selecionados, setSelecionados] = useState(new Set());
  const [servico, setServico] = useState('');
  const [mensagemCustomizada, setMensagemCustomizada] = useState('');
  const [dataDisparo, setDataDisparo] = useState('');
  const [horaDisparo, setHoraDisparo] = useState('');
  const [enviandoMassa, setEnviandoMassa] = useState(false);
  const [resultadoMassa, setResultadoMassa] = useState(null);

  // Modelos salvos pelo próprio profissional (mesma tabela usada no envio individual).
  const [templatesSalvos, setTemplatesSalvos] = useState([]);
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const [resClientes, resTemplates] = await Promise.all([
          clienteService.listar(token),
          lembreteService.listarTemplates(token).catch(() => ({ data: [] })),
        ]);
        setClientes(resClientes.data || []);
        setTemplatesSalvos(resTemplates.data || []);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, []);

  const buscaNormalizada = busca.trim().toLowerCase();
  const buscaDigitos = busca.replace(/\D/g, '');
  const clientesFiltrados = clientes.filter(cliente => {
    if (!buscaNormalizada) return true;
    const nomeCombina = cliente.nome?.toLowerCase().includes(buscaNormalizada);
    const telefoneCombina = buscaDigitos && cliente.telefone?.includes(buscaDigitos);
    return nomeCombina || telefoneCombina;
  });

  const alternarSelecionado = (id) => {
    setSelecionados(prev => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const marcarTodos = () => {
    if (selecionados.size === clientesFiltrados.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(clientesFiltrados.map(c => c.id)));
    }
  };

  const handleSelecionarMensagemPredefinida = (e) => {
    if (e.target.value) setMensagemCustomizada(e.target.value);
  };

  const handleSalvarComoModelo = async () => {
    if (!mensagemCustomizada.trim()) {
      return alert('Escreva a mensagem antes de salvar como modelo.');
    }
    const titulo = window.prompt('Dê um nome pra esse modelo (ex: "Confirmação de horário"):');
    if (!titulo || !titulo.trim()) return;

    setSalvandoTemplate(true);
    try {
      const token = await auth.currentUser.getIdToken();
      await lembreteService.criarTemplate({ titulo, mensagem: mensagemCustomizada }, token);
      const res = await lembreteService.listarTemplates(token);
      setTemplatesSalvos(res.data || []);
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao salvar o modelo.');
    } finally {
      setSalvandoTemplate(false);
    }
  };

  const handleExcluirTemplate = async (id) => {
    if (!window.confirm('Excluir esse modelo salvo?')) return;
    try {
      const token = await auth.currentUser.getIdToken();
      await lembreteService.excluirTemplate(id, token);
      setTemplatesSalvos((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao excluir o modelo.');
    }
  };

  const handleEnviarMassa = async (e) => {
    e.preventDefault();
    if (selecionados.size === 0) return alert('Selecione ao menos um cliente.');
    if (!dataDisparo || !horaDisparo) return alert('Informe a data e hora de disparo.');

    setEnviandoMassa(true);
    setResultadoMassa(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const clientesSelecionados = clientes
        .filter(c => selecionados.has(c.id))
        .map(c => ({ nome: c.nome, telefone: c.telefone }));

      const res = await lembreteService.agendarEmMassa({
        clientes: clientesSelecionados,
        servico,
        mensagem_customizada: mensagemCustomizada,
        data_disparo: dataDisparo,
        hora_disparo: horaDisparo,
      }, token);

      setResultadoMassa(res.data);
      setSelecionados(new Set());
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao enviar lembretes em massa.');
    } finally {
      setEnviandoMassa(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="animate-spin text-[#1883FF]" size={24} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center gap-3 mb-6 border-b border-purple-50 pb-4">
        <Send size={18} className="text-[#1883FF]" />
        <div>
          <h3 className="text-sm font-black uppercase italic text-slate-900 m-0">Lembretes</h3>
          <p className="text-[10px] font-semibold text-slate-500 m-0 mt-1">Envie individualmente ou em massa, buscando pelos seus clientes.</p>
        </div>
      </div>

      <div className="flex bg-slate-100 p-1 rounded-xl mb-6 w-full max-w-[320px] box-border">
        <button type="button" className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wider border-none rounded-lg cursor-pointer transition-all ${modo === 'individual' ? 'bg-white text-[#6D28D9] shadow-sm' : 'bg-transparent text-slate-400'}`} onClick={() => setModo('individual')}>Individual</button>
        <button type="button" className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wider border-none rounded-lg cursor-pointer transition-all ${modo === 'massa' ? 'bg-white text-[#6D28D9] shadow-sm' : 'bg-transparent text-slate-400'}`} onClick={() => setModo('massa')}>Em Massa</button>
      </div>

      <div className="flex items-center gap-3 bg-[#F8F7FF] border-2 border-transparent rounded-2xl px-4 py-1 mb-4 transition-all duration-300 focus-within:bg-white focus-within:border-[#6D28D9]/30 focus-within:ring-4 focus-within:ring-[#6D28D9]/10">
        <Search size={16} className="text-slate-400 shrink-0" />
        <input
          type="text"
          className="w-full py-3 border-none bg-transparent outline-none text-sm font-semibold text-slate-900 placeholder-slate-400"
          placeholder="Buscar por nome ou telefone..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {modo === 'massa' && clientesFiltrados.length > 0 && (
        <button
          type="button"
          onClick={marcarTodos}
          className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-[#6D28D9] bg-[#F5F3FF] border-none px-3 py-2 rounded-lg cursor-pointer hover:bg-[#EDE9FE] transition-colors mb-4"
        >
          {selecionados.size === clientesFiltrados.length ? <CheckSquare size={12} /> : <Square size={12} />}
          {selecionados.size === clientesFiltrados.length ? 'Desmarcar todos' : 'Marcar todos'}
        </button>
      )}

      {clientesFiltrados.length === 0 ? (
        <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-2xl p-10 flex flex-col items-center justify-center text-center mb-6">
          <Search size={32} className="text-slate-300 mb-3" />
          <p className="text-sm font-bold text-slate-500 mb-1">Nenhum cliente encontrado.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 mb-6 max-h-[320px] overflow-y-auto pr-1">
          {clientesFiltrados.map(cliente => (
            <div
              key={cliente.id}
              onClick={() => modo === 'individual' ? setClienteLembrete(cliente) : alternarSelecionado(cliente.id)}
              className="flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 cursor-pointer hover:border-purple-100 hover:bg-[#F5F3FF]/30 transition-all"
            >
              <div className="flex items-center gap-3 min-w-0">
                {modo === 'massa' && (
                  selecionados.has(cliente.id) ? <CheckSquare size={16} className="text-[#6D28D9] shrink-0" /> : <Square size={16} className="text-slate-300 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-black text-slate-800 m-0 truncate">{cliente.nome}</p>
                  <p className="flex items-center gap-1 text-[10px] font-bold text-slate-400 m-0">
                    <Phone size={10} /> {formatarTelefoneExibicao(cliente.telefone)}
                  </p>
                </div>
              </div>
              {modo === 'individual' && <Send size={14} className="text-slate-300 shrink-0" />}
            </div>
          ))}
        </div>
      )}

      {modo === 'massa' && (
        <form onSubmit={handleEnviarMassa} className="bg-[#F8F7FF] border border-purple-50 rounded-2xl p-4 sm:p-5 flex flex-col gap-4">
          <h4 className="text-xs font-black uppercase text-slate-700 m-0 flex items-center gap-1.5">
            <MessageSquare size={14} className="text-[#6D28D9]" /> Mensagem da Campanha
          </h4>

          <div className="flex flex-col gap-1.5 text-left">
            <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Tag size={12} /> Serviço/Contexto (Opcional)</label>
            <input type="text" className="w-full bg-white border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10" placeholder="Ex: Promoção de Aniversário da Loja" value={servico} onChange={e => setServico(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5 text-left">
            <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><MessageSquare size={12} /> Escolher Mensagem Pronta (Opcional)</label>
            <select className="w-full bg-white border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-semibold text-slate-700 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 cursor-pointer" onChange={handleSelecionarMensagemPredefinida} defaultValue="">
              <option value="">-- Selecione um modelo pronto para preencher abaixo --</option>
              {templatesSalvos.length > 0 && (
                <optgroup label="Meus modelos salvos">
                  {templatesSalvos.map((tpl) => <option key={tpl.id} value={tpl.mensagem}>{tpl.titulo}</option>)}
                </optgroup>
              )}
              <optgroup label="Sugestões">
                {MENSAGENS_PREDEFINIDAS.map((msg, idx) => <option key={idx} value={msg.texto}>{msg.titulo}</option>)}
              </optgroup>
            </select>
            {templatesSalvos.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {templatesSalvos.map((tpl) => (
                  <span key={tpl.id} className="inline-flex items-center gap-1 bg-white border border-purple-100 text-[#6D28D9] text-[10px] font-bold px-2.5 py-1 rounded-lg">
                    {tpl.titulo}
                    <button type="button" onClick={() => handleExcluirTemplate(tpl.id)} className="bg-transparent border-none p-0 cursor-pointer text-slate-300 hover:text-red-500 flex items-center" title="Excluir modelo">
                      <Trash2 size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5 text-left">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><MessageSquare size={12} /> Mensagem Customizada</label>
              <button type="button" onClick={handleSalvarComoModelo} disabled={salvandoTemplate} className="flex items-center gap-1 bg-transparent border-none text-[10px] font-black uppercase text-[#6D28D9] cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed px-0">
                {salvandoTemplate ? <Loader2 size={11} className="animate-spin" /> : <Bookmark size={11} />} Salvar como modelo
              </button>
            </div>
            <textarea className="w-full bg-white border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 min-h-[90px] resize-y font-sans" placeholder="Use {nome} e {servico} pra personalizar por cliente..." value={mensagemCustomizada} onChange={e => setMensagemCustomizada(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Calendar size={12} /> Data de Disparo</label>
              <input type="date" className="w-full bg-white border border-purple-50 py-3 px-3 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10" value={dataDisparo} onChange={e => setDataDisparo(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5 text-left">
              <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Zap size={12} /> Hora de Disparo</label>
              <input type="time" className="w-full bg-white border border-purple-50 py-3 px-3 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10" value={horaDisparo} onChange={e => setHoraDisparo(e.target.value)} required />
            </div>
          </div>

          <button
            type="submit" disabled={enviandoMassa || selecionados.size === 0}
            className="w-full h-14 border-none rounded-2xl text-white text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2.5 cursor-pointer transition-all duration-300 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90 hover:-translate-y-0.5 shadow-lg shadow-[#6D28D9]/20"
          >
            {enviandoMassa ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {enviandoMassa ? 'Enviando...' : `Enviar para ${selecionados.size} cliente${selecionados.size === 1 ? '' : 's'}`}
          </button>

          {resultadoMassa && (
            <div className="bg-white border border-dashed border-emerald-300 rounded-xl p-4 flex flex-col gap-2">
              <p className="flex items-center gap-1.5 text-xs font-black text-emerald-600 m-0">
                <CheckCircle2 size={14} /> {resultadoMassa.enviados} lembrete(s) programado(s) com sucesso.
              </p>
              {resultadoMassa.falharam?.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="flex items-center gap-1.5 text-xs font-black text-red-500 m-0">
                    <AlertCircle size={14} /> {resultadoMassa.falharam.length} falharam:
                  </p>
                  {resultadoMassa.falharam.map((f, i) => (
                    <p key={i} className="text-[10px] text-slate-500 font-semibold ml-5 m-0">{f.nome || f.telefone}: {f.motivo}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
      )}

      {clienteLembrete && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[700px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm m-0 flex items-center gap-2">
                <Send size={16} className="text-[#1883FF]" /> Enviar Lembrete para {clienteLembrete.nome}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setClienteLembrete(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto pr-1">
              <CriarLembreteAvulso clientePreSelecionado={{ nome: clienteLembrete.nome, telefone: clienteLembrete.telefone }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LembretesMarketingTab;
